import React from 'react'
import { FiUsers } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { calculateDateRange } from '../../utils/dateUtils'
import AnalyticsLoadingOverlay from './AnalyticsLoadingOverlay'

export default function StudentsIncludedSummary({ data, loading = false, daysFilter, scope, scopeLabel }) {
    const { t, i18n } = useTranslation()
    const included = data?.studentsIncluded
    const checkedIn = data?.studentsCheckedIn
    const available = !loading && Number.isInteger(included) && Number.isInteger(checkedIn)
    const percentage = available && included > 0 ? Math.round(checkedIn / included * 100) : 0
    const number = (value) => new Intl.NumberFormat(i18n?.language).format(value)
    const selected = (scope?.selected || []).flat()
    const yearsOnly = selected.length > 0 && selected.every((item) => item.field === 'classes.year')
    let groups = t(scope?.all ? 'All accessible students' : 'Selected student groups')
    if (scope?.yearGroup) groups = `${t('Year')} ${scope.yearGroup}`
    else if (!scope?.all && yearsOnly) groups = [...new Set(selected.map((item) => item.code))].map((year) => `${t('Year')} ${year}`).join(', ')
    if (scopeLabel) groups = scopeLabel
    const { startDate, endDate } = calculateDateRange(daysFilter)
    const period = daysFilter.code === 'custom' ? `${startDate} – ${endDate}` : t(daysFilter.label)

    return (
        <section aria-label={t('Students included')} aria-busy={loading} className="bg-white rounded-[24px] p-6 mt-4 text-darkenPurple relative">
            <div aria-hidden={loading || undefined} className={`flex flex-wrap items-center gap-6 ${loading ? 'blur pointer-events-none select-none' : ''}`}>
                <div className="flex items-center gap-4 flex-1 basis-[260px] min-w-0">
                    <div className="bg-[#ECEBF8] rounded-[16px] p-4 shrink-0" aria-hidden="true"><FiUsers size={24} color="#77768D" /></div>
                    <div className="min-w-0">
                        <p className="text-[16px] font-semibold">{t('Students included')}</p>
                        {loading ? <div className="h-11 w-28 rounded bg-lightBG animate-pulse" /> : <p className="text-[36px] leading-[44px] font-semibold" aria-live="polite">{available ? number(included) : '—'}</p>}
                        <p className="text-[13px] text-strongGray break-words">{groups} · {period}</p>
                    </div>
                </div>
                <div className="flex-1 basis-[260px] min-w-0">
                    {loading ? (
                        <div className="animate-pulse" aria-hidden="true">
                            <div className="h-8 w-3/4 rounded bg-lightBG" />
                            <div className="h-2 w-full rounded-full bg-lightBG my-2" />
                            <div className="h-4 w-4/5 rounded bg-lightBG" />
                        </div>
                    ) : available ? (
                        <>
                            <p className="text-[14px] text-strongGray"><span className="text-[24px] font-semibold text-darkenPurple">{number(checkedIn)}</span> {t('checked in during this period')}</p>
                            <div role="progressbar" aria-label={t('Students who checked in')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage} className="h-2 bg-[#ECEBF8] rounded-full overflow-hidden my-2">
                                <div className="h-full bg-[#7C82C3] rounded-full" style={{ width: `${percentage}%` }} />
                            </div>
                            <p className="text-[13px] text-strongGray">{t("{{percentage}}% of included students · {{count}} haven't checked in", { percentage, count: number(included - checkedIn) })}</p>
                        </>
                    ) : <p role="status" className="text-[14px] text-strongGray">{t('Unable to load student count')}</p>}
                </div>
            </div>
            {loading && <AnalyticsLoadingOverlay />}
        </section>
    )
}
